import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import type { CalendarItem } from '../../../shared/types';
import { toLocalInputValue } from '../../lib/format';
import { cn } from '../../lib/utils';
import { buildMonthGrid, dayKeyFromLocalInput, WEEKDAY_LABELS } from './calendarDate';
import { CalendarItemPill } from './CalendarItemPill';

const MAX_VISIBLE = 3;

export interface CalendarGridProps {
  year: number;
  /** 1-12 */
  month: number;
  items: CalendarItem[];
  timezone: string;
  expandedDays: Set<string>;
  onToggleExpand: (dayKey: string) => void;
  onSelectItem: (item: CalendarItem) => void;
  onAddDay: (dayKey: string) => void;
  className?: string;
}

function itemTimestamp(item: CalendarItem): number {
  return item.scheduledAt ?? item.publishedAt ?? 0;
}

/**
 * Month grid: six weeks of seven days, platform-colored pills per scheduled
 * or published post, an empty-day add affordance, and a "+N more" expander.
 * Owned by WEB-3.
 */
export function CalendarGrid({
  year,
  month,
  items,
  timezone,
  expandedDays,
  onToggleExpand,
  onSelectItem,
  onAddDay,
  className,
}: CalendarGridProps) {
  const cells = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    for (const item of items) {
      const ms = itemTimestamp(item);
      if (!ms) continue;
      const key = dayKeyFromLocalInput(toLocalInputValue(ms, timezone));
      const bucket = map.get(key);
      if (bucket) bucket.push(item);
      else map.set(key, [item]);
    }
    for (const bucket of map.values()) bucket.sort((a, b) => itemTimestamp(a) - itemTimestamp(b));
    return map;
  }, [items, timezone]);

  const todayKey = useMemo(() => dayKeyFromLocalInput(toLocalInputValue(Date.now(), timezone)), [timezone]);

  return (
    <div className={cn('pap-card overflow-hidden', className)}>
      <div className="grid grid-cols-7 border-b border-border bg-elevated/40">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="px-2 py-2 text-center text-[11px] font-medium uppercase tracking-wide text-subtle">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell) => {
          const dayItems = byDay.get(cell.key) ?? [];
          const isToday = cell.key === todayKey;
          const expanded = expandedDays.has(cell.key);
          const visible = expanded ? dayItems : dayItems.slice(0, MAX_VISIBLE);
          const hidden = dayItems.length - visible.length;

          return (
            <div
              key={cell.key}
              className={cn(
                'group flex min-h-28 flex-col gap-1 border-b border-r border-border p-1.5 last:border-r-0',
                '[&:nth-child(7n)]:border-r-0',
                !cell.currentMonth && 'bg-elevated/20',
              )}
            >
              <div className="flex items-center justify-between px-0.5">
                <span
                  className={cn(
                    'pap-tnum inline-flex size-5 items-center justify-center rounded-full text-[11px] font-medium',
                    isToday
                      ? 'bg-accent text-accent-fg'
                      : cell.currentMonth
                        ? 'text-fg'
                        : 'text-subtle',
                  )}
                >
                  {cell.day}
                </span>
                {dayItems.length === 0 && (
                  <button
                    type="button"
                    onClick={() => onAddDay(cell.key)}
                    aria-label="Add a post on this day"
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full text-subtle opacity-0 transition-opacity',
                      'hover:bg-elevated hover:text-fg focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring',
                      'group-hover:opacity-100',
                    )}
                  >
                    <Plus className="size-3.5" />
                  </button>
                )}
              </div>
              <div className="flex flex-1 flex-col gap-0.5">
                {visible.map((item) => (
                  <CalendarItemPill
                    key={item.platformPostId}
                    item={item}
                    timezone={timezone}
                    onClick={() => onSelectItem(item)}
                  />
                ))}
                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => onToggleExpand(cell.key)}
                    className="px-1.5 py-0.5 text-left text-[11px] font-medium text-accent hover:underline"
                  >
                    +{hidden} more
                  </button>
                )}
                {expanded && dayItems.length > MAX_VISIBLE && (
                  <button
                    type="button"
                    onClick={() => onToggleExpand(cell.key)}
                    className="px-1.5 py-0.5 text-left text-[11px] font-medium text-subtle hover:text-fg"
                  >
                    Show less
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
